import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import { useToast } from "@/shared/toast/useToast";
import type { ComplianceRun } from "@/features/stationAdmin/api/iaComplianceApi";
import {
  completedRunOutcome,
  formatShortDateTime,
  runStatusChip,
} from "./complianceView";
import { exportComplianceHistoryCsv } from "./complianceReportCsv";

interface ComplianceRunsCardProps {
  runs: ComplianceRun[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  selectedRunId: string | null;
  onSelect: (runId: string) => void;
  /** Loads every history page only when the user explicitly exports it. */
  onLoadAllRuns: () => Promise<ComplianceRun[]>;
  /** Station name included in the exported spreadsheet (optional). */
  stationName?: string;
  /** Live detail for the selected run — fresher than the list snapshot while
   *  the run is being polled. */
  activeRun?: ComplianceRun;
}

/**
 * Compliance-run history (newest first; the UI displays the first server
 * page). Selecting a run shows its per-report results below. Status/progress
 * for the selected run come from the polled detail when available, so the
 * list doesn't lag it. Export explicitly walks every server page.
 */
export const ComplianceRunsCard: React.FC<ComplianceRunsCardProps> = ({
  runs,
  isLoading,
  isError,
  onRetry,
  selectedRunId,
  onSelect,
  onLoadAllRuns,
  stationName,
  activeRun,
}) => {
  const toast = useToast();
  const [exporting, setExporting] = useState(false);
  const canExport = !isLoading && !isError && runs.length > 0 && !exporting;

  const handleExport = async () => {
    if (!canExport) return;
    setExporting(true);
    try {
      const allRuns = await onLoadAllRuns();
      const runById = new Map(allRuns.map((run) => [run.runId, run]));
      // Preserve any just-created rows already visible in the UI and only let
      // a polled detail replace the export fetch when it is demonstrably newer.
      for (const candidate of activeRun ? [...runs, activeRun] : runs) {
        const fetched = runById.get(candidate.runId);
        if (
          !fetched ||
          Date.parse(candidate.updatedAt) > Date.parse(fetched.updatedAt)
        ) {
          runById.set(candidate.runId, candidate);
        }
      }
      const exportRuns = [...runById.values()].sort((a, b) =>
        Date.parse(b.createdAt) - Date.parse(a.createdAt),
      );
      exportComplianceHistoryCsv({ runs: exportRuns, stationName });
    } catch {
      toast.error("Couldn't export check history. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Check History
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Select a check to see its results
            </Typography>
          </Box>
          <Tooltip title="Export all check history as a CSV spreadsheet">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  exporting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <FileDownload fontSize="small" />
                  )
                }
                onClick={() => void handleExport()}
                disabled={!canExport}
                aria-label="Export all check history to CSV"
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
        </Box>

        {isError ? (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            Failed to load compliance checks.
          </Alert>
        ) : isLoading ? (
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
          </Stack>
        ) : runs.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, fontStyle: "italic" }}
          >
            No compliance checks yet.
          </Typography>
        ) : (
          <List disablePadding sx={{ mt: 1 }}>
            {runs.map((listRun) => {
              const run =
                activeRun && activeRun.runId === listRun.runId
                  ? activeRun
                  : listRun;
              const chip = runStatusChip(run.status);
              const running = run.status === "queued" || run.status === "running";
              const outcome =
                run.status === "completed" ? completedRunOutcome(run) : null;
              return (
                <ListItemButton
                  key={run.runId}
                  selected={run.runId === selectedRunId}
                  onClick={() => onSelect(run.runId)}
                  sx={{ borderRadius: 2, alignItems: "flex-start", py: 1 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        {formatShortDateTime(run.createdAt)}
                      </Typography>
                      <Chip
                        size="small"
                        color={chip.color}
                        label={chip.label}
                        icon={
                          chip.inProgress ? (
                            <CircularProgress size={12} color="inherit" />
                          ) : undefined
                        }
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                      {run.status === "completed" && (
                        <Typography
                          variant="caption"
                          color={`${outcome?.tone ?? "success"}.main`}
                          fontWeight={600}
                        >
                          {outcome?.compact}
                        </Typography>
                      )}
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: "block" }}
                    >
                      {run.reportCount}{" "}
                      {run.reportCount === 1 ? "report" : "reports"} ·{" "}
                      {run.policyDocNames.join(", ")}
                    </Typography>
                    {running && (
                      <LinearProgress
                        variant="determinate"
                        value={
                          run.reportCount > 0
                            ? (run.checkedCount / run.reportCount) * 100
                            : 0
                        }
                        sx={{ mt: 0.75, borderRadius: 1 }}
                      />
                    )}
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
};
